import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Clock, Database, ShieldCheck } from 'lucide-react';

const DeleteAccountPage = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-4"
            data-testid="delete-account-back-btn"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Caveat", cursive' }}>
            Suppression de compte
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">

        {/* Introduction */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="delete-intro">
          <div className="flex items-center mb-4">
            <Trash2 className="w-7 h-7 text-red-400 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Supprimer votre compte Dis Maman!</h2>
          </div>
          <p className="text-gray-600">
            Vous pouvez demander la suppression de votre compte et de vos données personnelles à tout moment.
            Cette page décrit la procédure à suivre, les données concernées et les délais de traitement.
          </p>
        </div>

        {/* Procédure */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="delete-procedure">
          <div className="flex items-center mb-4">
            <Mail className="w-6 h-6 text-teal-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Comment demander la suppression</h2>
          </div>
          <div className="text-gray-600 space-y-4">
            <p>Pour demander la suppression de votre compte, envoyez un email à :</p>
            <a 
              href="mailto:contact@dismaman.com?subject=Demande%20de%20suppression%20de%20compte%20Dis%20Maman!"
              className="inline-flex items-center px-5 py-3 bg-teal-50 text-teal-700 rounded-xl font-bold hover:bg-teal-100 transition-colors"
              data-testid="delete-email-link"
            >
              <Mail className="w-5 h-5 mr-2" />
              contact@dismaman.com
            </a>
            <p className="font-medium">Votre email doit contenir :</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Objet</strong> : "Demande de suppression de compte"</li>
              <li><strong>L'adresse email</strong> associée à votre compte Dis Maman!</li>
              <li><strong>Confirmation</strong> que vous souhaitez la suppression définitive de votre compte et de vos données</li>
            </ul>
          </div>
        </div>

        {/* Données supprimées */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="delete-data-deleted">
          <div className="flex items-center mb-4">
            <Database className="w-6 h-6 text-red-400 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Données supprimées</h2>
          </div>
          <p className="text-gray-600 mb-3">
            Lors de la suppression de votre compte, les données suivantes seront <strong>définitivement effacées</strong> :
          </p>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Informations de compte</strong> : adresse email, nom, prénom</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Profils enfants</strong> : prénoms, âges, préférences</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Historique de conversations</strong> : toutes les questions posées et réponses générées</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Histoires créées</strong> : toutes les histoires et illustrations générées</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Données de quiz</strong> : historique de révisions et résultats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400 font-bold mt-0.5">x</span>
              <span><strong>Tokens de notification</strong> : identifiants push de vos appareils</span>
            </li>
          </ul>
        </div>

        {/* Données conservées */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="delete-data-retained">
          <div className="flex items-center mb-4">
            <ShieldCheck className="w-6 h-6 text-amber-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Données conservées temporairement</h2>
          </div>
          <p className="text-gray-600 mb-3">
            Certaines données peuvent être conservées pour une durée limitée après la suppression, conformément à nos obligations légales :
          </p>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Données de facturation</strong> : conservées pendant <strong>6 ans</strong> après la dernière transaction, conformément aux obligations fiscales et comptables françaises (article L123-22 du Code de commerce).</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Logs de sécurité</strong> : conservés pendant <strong>12 mois</strong> maximum, conformément aux exigences de la CNIL.</span>
            </li>
          </ul>
          <p className="text-gray-500 text-sm mt-4">
            Ces données conservées sont anonymisées dans la mesure du possible et ne sont accessibles qu'en cas de contrôle légal.
          </p>
        </div>

        {/* Délai */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-lg" data-testid="delete-timeline">
          <div className="flex items-center mb-4">
            <Clock className="w-6 h-6 text-teal-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Délai de traitement</h2>
          </div>
          <div className="text-gray-600 space-y-3">
            <p>
              Votre demande sera traitée dans un délai maximum de <strong>30 jours</strong> à compter de la réception de votre email, conformément au RGPD (article 17).
            </p>
            <p>
              Vous recevrez un email de confirmation une fois la suppression effectuée.
            </p>
            <p className="text-sm text-gray-500">
              Si vous disposez d'un abonnement actif, celui-ci sera résilié automatiquement. Aucun remboursement ne sera effectué pour la période en cours.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-red-400 to-red-500 rounded-2xl p-6 text-white shadow-lg mb-8" data-testid="delete-cta">
          <h2 className="text-xl font-bold mb-3">Prêt à supprimer votre compte ?</h2>
          <p className="mb-4 text-white/90">
            Envoyez votre demande par email. Cette action est irréversible.
          </p>
          <a 
            href="mailto:contact@dismaman.com?subject=Demande%20de%20suppression%20de%20compte%20Dis%20Maman!&body=Bonjour%2C%0A%0AJe%20souhaite%20supprimer%20d%C3%A9finitivement%20mon%20compte%20Dis%20Maman!%20associ%C3%A9%20%C3%A0%20cette%20adresse%20email.%0A%0AJe%20confirme%20vouloir%20la%20suppression%20de%20toutes%20mes%20donn%C3%A9es%20personnelles.%0A%0AMerci."
            className="inline-flex items-center px-6 py-3 bg-white text-red-500 rounded-xl font-bold hover:bg-gray-100 transition-colors"
            data-testid="delete-send-email-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            Envoyer la demande
          </a>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm pb-8">
          <p>Dis Maman! - Application conforme au RGPD</p>
          <p>Dernière mise à jour : Avril 2026</p>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
